import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import membershipApi from '../services/membershipApi';
import { useAuth } from './AuthContext';

const MembershipContext = createContext();

export const useMembership = () => useContext(MembershipContext);

export const MembershipProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [currentMembership, setCurrentMembership] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [membershipConfig, setMembershipConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMembershipData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [membershipRes, creditsRes, configRes] = await Promise.all([
        membershipApi.getCurrentMembership(),
        membershipApi.getCreditBalance(),
        membershipApi.getMembershipConfig()
      ]);

      setCurrentMembership(membershipRes.data);
      setCreditBalance(creditsRes.data);
      setMembershipConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching membership data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchMembershipData();
  }, [fetchMembershipData]);

  const refreshMembership = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const response = await membershipApi.getCurrentMembership();
      setCurrentMembership(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing membership:', error);
      return null;
    }
  }, [isAuthenticated]);

  const refreshCreditBalance = useCallback(async () => {
    try {
      const response = await membershipApi.getCreditBalance();
      setCreditBalance(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing credit balance:', error);
      return null;
    }
  }, []);

  // Start a Stripe Checkout flow. Resolves with { success, url? } so callers
  // can decide how to handle redirects (we redirect by default).
  const startCheckout = useCallback(async (tier, interval, { autoRedirect = true } = {}) => {
    try {
      const response = await membershipApi.createCheckoutSession(tier, interval);
      const url = response?.data?.url;
      if (!url) {
        return { success: false, error: new Error('No checkout URL returned') };
      }
      if (autoRedirect) {
        window.location.href = url;
      }
      return { success: true, url };
    } catch (error) {
      console.error('Error starting checkout session:', error);
      return { success: false, error };
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    try {
      const response = await membershipApi.cancelSubscription();
      await refreshMembership();
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return { success: false, error };
    }
  }, [refreshMembership]);

  const resumeSubscription = useCallback(async () => {
    try {
      const response = await membershipApi.resumeSubscription();
      await refreshMembership();
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error resuming subscription:', error);
      return { success: false, error };
    }
  }, [refreshMembership]);

  const purchaseCredits = useCallback(async (amount) => {
    try {
      const response = await membershipApi.purchaseCredits(amount);
      setCreditBalance(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error purchasing credits:', error);
      return { success: false, error };
    }
  }, []);

  const value = {
    currentMembership,
    creditBalance,
    membershipConfig,
    loading,
    refreshMembership,
    refreshCreditBalance,
    startCheckout,
    cancelSubscription,
    resumeSubscription,
    purchaseCredits
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};
